import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Clock, Calendar, UserPlus, Phone, MessageCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";

const NotificationsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const response = await api.get("/notifications", token);
        setNotifications(response.data);
      } catch (error) {
        toast.error("خطأ في تحميل التنبيهات");
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();
  }, [token]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-pulse-soft text-gray-500">جاري التحميل...</div></div>;
  }

  const leadNotifications = notifications.filter(n => n.type === "lead");
  const regularNotifications = notifications.filter(n => n.type !== "lead");
  const todayNotifications = regularNotifications.filter(n => n.days_remaining === 0);
  const tomorrowNotifications = regularNotifications.filter(n => n.days_remaining === 1);
  const upcomingNotifications = regularNotifications.filter(n => n.days_remaining > 1);

  return (
    <div className="animate-fade-in" data-testid="notifications-page">
      <div className="page-header">
        <h1 className="page-title">التنبيهات والمراجعات</h1>
        <p className="page-subtitle">جميع المواعيد والمراجعات القادمة خلال الأسبوع</p>
      </div>

      {notifications.length === 0 ? (
        <div className="empty-state data-table">
          <Bell className="empty-state-icon" />
          <h3>لا توجد تنبيهات</h3>
          <p>لا توجد مواعيد أو مراجعات قادمة خلال الأسبوع القادم</p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Leads / عملاء محتملين */}
          {leadNotifications.length > 0 && (
            <div className="data-table">
              <div className="table-header bg-amber-50">
                <h3 className="table-title flex items-center gap-2 text-amber-700">
                  <UserPlus className="w-5 h-5" />
                  عملاء محتملين ({leadNotifications.length})
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {leadNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="flex items-center justify-between p-3 rounded-xl bg-amber-50 border border-amber-200"
                    data-testid={`notif-lead-${notif.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center text-white">
                        <UserPlus className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-bold text-gray-800">{notif.message}</p>
                        <p className="text-xs text-gray-500">{notif.date} • قبل {Math.abs(notif.days_remaining)} يوم</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {notif.message.includes("07") && (
                        <>
                          <a href={`tel:${notif.message.match(/\d{10,}/)?.[0] || ""}`} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Phone className="w-4 h-4" /></a>
                          <a href={`https://wa.me/${(notif.message.match(/\d{10,}/)?.[0] || "").replace(/^0/, "964")}`} target="_blank" rel="noreferrer" className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"><MessageCircle className="w-4 h-4" /></a>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Today */}
          {todayNotifications.length > 0 && (
            <div className="data-table">
              <div className="table-header bg-red-50">
                <h3 className="table-title flex items-center gap-2 text-red-600">
                  <Bell className="w-5 h-5 animate-pulse" />
                  اليوم ({todayNotifications.length})
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {todayNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="appointment-card cursor-pointer bg-red-50 border border-red-100"
                    onClick={() => navigate(`/patients/${notif.patient_id}`)}
                    data-testid={`notif-today-${notif.id}`}
                  >
                    <div className="appointment-date bg-red-100 border-red-200">
                      <div className="day text-red-600">{new Date(notif.date).getDate()}</div>
                      <div className="month text-red-500">{new Date(notif.date).toLocaleDateString('ar-EG', { month: 'short' })}</div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{notif.patient_name}</h4>
                      <p className="text-sm text-red-600 font-medium">{notif.message}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      notif.type === "followup" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      {notif.type === "followup" ? "مراجعة" : "موعد"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tomorrow */}
          {tomorrowNotifications.length > 0 && (
            <div className="data-table">
              <div className="table-header bg-amber-50">
                <h3 className="table-title flex items-center gap-2 text-amber-600">
                  <Clock className="w-5 h-5" />
                  غداً ({tomorrowNotifications.length})
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {tomorrowNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="appointment-card cursor-pointer bg-amber-50 border border-amber-100"
                    onClick={() => navigate(`/patients/${notif.patient_id}`)}
                    data-testid={`notif-tomorrow-${notif.id}`}
                  >
                    <div className="appointment-date bg-amber-100 border-amber-200">
                      <div className="day text-amber-600">{new Date(notif.date).getDate()}</div>
                      <div className="month text-amber-500">{new Date(notif.date).toLocaleDateString('ar-EG', { month: 'short' })}</div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{notif.patient_name}</h4>
                      <p className="text-sm text-amber-600">{notif.message}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      notif.type === "followup" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      {notif.type === "followup" ? "مراجعة" : "موعد"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Upcoming */}
          {upcomingNotifications.length > 0 && (
            <div className="data-table">
              <div className="table-header">
                <h3 className="table-title flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  قادم ({upcomingNotifications.length})
                </h3>
              </div>
              <div className="p-4 space-y-3">
                {upcomingNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    className="appointment-card cursor-pointer"
                    onClick={() => navigate(`/patients/${notif.patient_id}`)}
                    data-testid={`notif-upcoming-${notif.id}`}
                  >
                    <div className="appointment-date">
                      <div className="day">{new Date(notif.date).getDate()}</div>
                      <div className="month">{new Date(notif.date).toLocaleDateString('ar-EG', { month: 'short' })}</div>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">{notif.patient_name}</h4>
                      <p className="text-sm text-gray-500">{notif.message}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      notif.type === "followup" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"
                    }`}>
                      {notif.type === "followup" ? "مراجعة" : "موعد"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
